/**
 * « En faire un défi privé » depuis l'écran d'accueil d'un exercice (migration 0722).
 * Le défi reprend l'exercice tel quel : ses premières questions (5 au plus), dans l'ordre,
 * chacune sur la feuille de brouillon. Une fenêtre résume les réglages : « Créer le défi »
 * le crée tout de suite et ouvre son salon ; « Modifier les réglages » ouvre le formulaire
 * prérempli (/live/prive/nouveau?exercise=…&card=…). Le serveur revérifie que l'exercice
 * est ouvert à l'organisateur (un compte gratuit : l'essai gratuit de la carte).
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { Radio, Swords, X } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { stripMath } from '@jeraisonne/db/cards';
import { useAuth } from '@/lib/auth';
import { mainCardOfLinks, useExerciseCardLinks } from '@/lib/cards/student-skillcards';
import { randomPresetPath, setPrivateLiveCover } from '@/lib/live-covers';
import {
  PRIVATE_LIVE_MAX_QUESTIONS,
  EXERCISE_CHALLENGE_SHEET_MS,
  privateLiveErrorMessage,
  sheetDurationLabel,
  useCreatePrivateLive,
  warmPrivateLivePreview,
  type CreatePrivateLiveInput,
} from '@/lib/private-live';

/** Titre du défi : « Défi : Théorème de Pythagore 🟢 🎁 » (80 caractères au plus). PUR. */
export function exerciseChallengeTitle(title: string): string {
  const clean = stripMath(title).replace(/\s+/g, ' ').trim();
  return (clean ? `Défi : ${clean}` : 'Défi').slice(0, 80);
}

/** Nombre de questions du défi : celles de l'exercice, 5 au plus. PUR. */
export function exerciseChallengeCount(totalQuestions: number): number {
  return Math.max(1, Math.min(PRIVATE_LIVE_MAX_QUESTIONS, Math.round(totalQuestions) || 1));
}

/** Demande de création d'un défi depuis un exercice (réglages par défaut). PUR. */
export function exerciseChallengeInput(exercise: {
  id: string;
  title: string;
  totalQuestions: number;
}): CreatePrivateLiveInput {
  return {
    level: '',
    subject: '',
    pack_id: null,
    card_ids: [],
    exercise_id: exercise.id,
    count: exerciseChallengeCount(exercise.totalQuestions),
    format: 'sheet',
    final_sheet: false,
    sheet_ms: EXERCISE_CHALLENGE_SHEET_MS,
    progressive: false,
    difficulty: 'medium',
    title: exerciseChallengeTitle(exercise.title),
    start: { mode: 'manual' },
  };
}

export function ExerciseChallengeButton({
  exercise,
}: {
  exercise: { id: string; title: string; totalQuestions: number };
}) {
  const { session } = useAuth();
  const isAccount = !!session && !session.user.is_anonymous;
  const links = useExerciseCardLinks(exercise.id, isAccount);
  const card = mainCardOfLinks(links.data ?? []);
  const [open, setOpen] = useState(false);
  if (!isAccount || !card) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border-ink-200 text-ink-700 font-display inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border bg-white px-6 py-3 text-sm font-bold active:scale-[.98] sm:w-auto"
      >
        <Swords className="h-4 w-4" aria-hidden />
        Mode Défi
      </button>
      {open && (
        <ExerciseChallengeDialog
          exercise={exercise}
          cardId={card.card_id}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ExerciseChallengeDialog({
  exercise,
  cardId,
  onClose,
}: {
  exercise: { id: string; title: string; totalQuestions: number };
  cardId: string;
  onClose: () => void;
}) {
  const create = useCreatePrivateLive();
  const [error, setError] = useState<string | null>(null);
  const input = exerciseChallengeInput(exercise);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const submit = () => {
    setError(null);
    create.mutate(input, {
      onSuccess: async (r) => {
        await setPrivateLiveCover(r.challenge_id, randomPresetPath()).catch(() => undefined);
        warmPrivateLivePreview(r.join_code);
        window.location.href = `/defi-live/${r.join_code}`;
      },
      onError: (e) => setError(privateLiveErrorMessage(e.message)),
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ex-challenge-title"
        className="ocean:bg-[#061433] pb-safe w-full max-w-md space-y-4 rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="ex-challenge-title" className="font-display text-ink-900 text-lg font-bold">
            En faire un défi privé
          </h2>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            className="text-ink-500 -mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <p className="text-ink-900 text-sm font-semibold">
          <MathText>{exercise.title}</MathText>
        </p>
        <ul className="text-ink-600 list-disc space-y-1 pl-5 text-sm">
          <li>
            {input.count} question{input.count > 1 ? 's' : ''} de l’exercice, dans l’ordre
          </li>
          <li>Sur la feuille de brouillon, {sheetDurationLabel(input.sheet_ms)} par question</li>
          <li>Tu lances le défi quand tout le monde est là</li>
          <li>Tes contacts sont prévenus ; partage le lien à qui tu veux</li>
        </ul>
        {error && (
          <p role="alert" className="text-error-600 text-sm">
            {error}
          </p>
        )}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={create.isPending}
            className="bg-accent-400 font-display inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl px-6 text-base font-bold text-[#0a2e57] active:scale-[.98] disabled:opacity-50"
          >
            <Radio className="h-4 w-4" aria-hidden />
            {create.isPending ? 'Création…' : 'Créer le défi'}
          </button>
          <Link
            to="/live/prive/nouveau"
            search={{ exercise: exercise.id, card: cardId }}
            className="text-primary-700 inline-flex min-h-11 w-full items-center justify-center rounded-2xl text-sm font-semibold"
          >
            Modifier les réglages
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}
